/**
 * Where to send someone after Auth0 sign-in, so deep links (alert emails,
 * shared profile links) survive the round trip.
 *
 * The path rides through Auth0 as appState.returnTo. Auth0 hands control back
 * before React Router knows the URL changed, so the path is kept in
 * sessionStorage until PostLoginRedirect can navigate there.
 */

const STORAGE_KEY = 'aiu-post-login-path';

type PathStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const sessionStore = (): PathStorage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/**
 * The path if it's a page in this app ("/persons/12?addRoleTag=5"), else
 * null. Rejects other hosts ("//evil.example", "https://...") and the login
 * page itself, which would loop.
 */
export const safeReturnPath = (path: string | null | undefined): string | null => {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
    return null;
  }
  if (/^\/login(?:[/?#]|$)/.test(path)) {
    return null;
  }
  return path;
};

export const rememberPostLoginPath = (
  path: string | null | undefined,
  storage: PathStorage | null = sessionStore()
): void => {
  const safe = safeReturnPath(path);
  if (!safe || !storage) return;
  try {
    storage.setItem(STORAGE_KEY, safe);
  } catch {
    // Storage blocked (private mode); the user lands on the home page instead
  }
};

/** Returns the remembered path once, then forgets it. */
export const takePostLoginPath = (storage: PathStorage | null = sessionStore()): string | null => {
  if (!storage) return null;
  try {
    const path = storage.getItem(STORAGE_KEY);
    storage.removeItem(STORAGE_KEY);
    return safeReturnPath(path);
  } catch {
    return null;
  }
};
